import React from 'react';
import styled, { css } from 'styled-components';

import colors from '../../style/colors';
import Text, { TextProps } from '../text/text';
import { H6, TitleProps } from '../title';
import BaseCard, { BaseCardProps } from './base-card';

const pastelVariants = {
  violet: css`
    background: ${colors.lightViolet};
    color: ${colors.darkViolet};
    border: 1px solid transparent;

    &:hover:not(.disabled):not([disabled]) {
      border-color: ${colors.darkViolet};
    }
  `,
  lavender: css`
    background: ${colors.lightLavender};
    color: ${colors.darkLavender};
    border: 1px solid transparent;

    &:hover:not(.disabled):not([disabled]) {
      border-color: ${colors.darkLavender};
    }
  `,
  sun: css`
    background: ${colors.lightSun};
    color: ${colors.darkSun};
    border: 1px solid transparent;

    &:hover:not(.disabled):not([disabled]) {
      border-color: ${colors.darkSun};
    }
  `,
  emerald: css`
    background: ${colors.lightEmerald};
    color: ${colors.darkEmerald};
    border: 1px solid transparent;

    &:hover:not(.disabled):not([disabled]) {
      border-color: ${colors.darkEmerald};
    }
  `,
  pumpkin: css`
    background: ${colors.lightPumpkin};
    color: ${colors.darkPumpkin};
    border: 1px solid transparent;

    &:hover:not(.disabled):not([disabled]) {
      border-color: ${colors.darkPumpkin};
    }
  `,
  teal: css`
    background: ${colors.lightTeal};
    color: ${colors.darkTeal};
    border: 1px solid transparent;

    &:hover:not(.disabled):not([disabled]) {
      border-color: ${colors.darkTeal};
    }
  `,
  sky: css`
    background: ${colors.actionLight};
    color: ${colors.darkSky};
    border: 1px solid transparent;

    &:hover:not(.disabled):not([disabled]) {
      border-color: ${colors.darkSky};
    }
  `,
  blank: css`
    background: ${colors.white};
    color: ${colors.gray900};
    border: 1px dashed ${colors.gray400};

    &:hover:not(.disabled):not([disabled]) {
      background: ${colors.surfaceTertiary};
      border-color: ${colors.gray900};
    }
  `,
};

export type PastelCardVariant = keyof typeof pastelVariants;

export interface PastelCardProps extends BaseCardProps {
  variant: PastelCardVariant;
}

const PastelCardRoot = styled(BaseCard).withConfig({
  shouldForwardProp: prop => prop !== 'variant',
})<PastelCardProps>`
  transition: all 250ms ease;
  cursor: pointer;

  ${({ variant }) => pastelVariants[variant]};

  &:focus-visible,
  &.active {
    border-color: ${colors.gray900};
  }

  &.disabled,
  &[disabled] {
    user-select: none;
    cursor: not-allowed;
    pointer-events: none;
    background: ${colors.gray100};
    border: 1px solid ${colors.gray100};
    /* Set on the root so PastelCard.Title / PastelCard.Text inherit the muted color. */
    color: ${colors.gray400};
  }
`;

const PastelCardDivider = styled.hr`
  border: 0;
  height: 1px;
  margin: 0;
  background-color: currentColor;
  opacity: 0.2;
`;

/**
 * Heading for a PastelCard. Uses the `inherit` color variant so it picks up the
 * card's variant color rather than applying its own default (and mutes when disabled).
 */
const PastelCardTitle = (props: TitleProps) => <H6 size="sm" variant="inherit" {...props} />;

/**
 * Body text for a PastelCard. Uses the `inherit` color variant so it picks up the
 * card's variant color rather than applying its own default (and mutes when disabled).
 */
const PastelCardText = (props: TextProps) => <Text variant="inherit" {...props} />;

const PastelCard = PastelCardRoot as typeof PastelCardRoot & {
  Divider: typeof PastelCardDivider;
  Title: typeof PastelCardTitle;
  Text: typeof PastelCardText;
};
PastelCard.Divider = PastelCardDivider;
PastelCard.Title = PastelCardTitle;
PastelCard.Text = PastelCardText;

export default PastelCard;
